import type { SVGProps } from 'react'

/**
 * Εικονίδια γραμμής, γραμμένα inline.
 *
 * Καμία βιβλιοθήκη: ένα πακέτο εικονιδίων κοστίζει δεκάδες kilobyte για τα
 * είκοσι σχήματα που χρησιμοποιούνται εδώ, και το bundle το κατεβάζει κάθε
 * μαγαζάτορας με σύνδεση κινητού. Όλα μοιράζονται το ίδιο πλέγμα 24×24 και
 * κληρονομούν το χρώμα του κειμένου, ώστε να αλλάζουν τόνο μαζί του.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 24, children, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  )
}

export function IconHome(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" />
      <path d="M9.5 21v-6h5v6" />
    </Svg>
  )
}

export function IconTruck(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2 6.5h11.5v10H2z" />
      <path d="M13.5 10H17l3 3v3.5h-6.5z" />
      <circle cx="7" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </Svg>
  )
}

export function IconCart(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2.5 3.5h2.2l2.3 11h10.4l2.1-8H6" />
      <circle cx="9" cy="19" r="1.6" />
      <circle cx="17" cy="19" r="1.6" />
    </Svg>
  )
}

export function IconClipboard(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 4.5H7a1 1 0 0 0-1 1V20a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5.5a1 1 0 0 0-1-1h-2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1" />
      <path d="M9 11h6M9 15h4" />
    </Svg>
  )
}

export function IconBell(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M18 15.5V10a6 6 0 1 0-12 0v5.5L4.5 18h15z" />
      <path d="M10 21h4" />
    </Svg>
  )
}

export function IconSettings(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" />
    </Svg>
  )
}

export function IconPlus(props: IconProps) {
  return (
    <Svg {...props} strokeWidth={2.25}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  )
}

export function IconStar({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <Svg {...props} fill={filled ? 'currentColor' : 'none'}>
      <path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.7l5.9-.8z" />
    </Svg>
  )
}

export function IconTrash(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 6.5h16M9.5 6.5V4.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
      <path d="M6.5 6.5 7.4 20a1 1 0 0 0 1 1h7.2a1 1 0 0 0 1-1l.9-13.5" />
      <path d="M10.5 10.5v6.5M13.5 10.5v6.5" />
    </Svg>
  )
}

export function IconClose(props: IconProps) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  )
}

export function IconSearch(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </Svg>
  )
}

export function IconClock(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Svg>
  )
}

export function IconCalendar(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </Svg>
  )
}

export function IconBox(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2.8 20.5 7v10L12 21.2 3.5 17V7z" />
      <path d="M3.5 7 12 11.4 20.5 7M12 11.4v9.8" />
    </Svg>
  )
}

export function IconAlert(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.5 21.5 20h-19z" />
      <path d="M12 9.5v4.5M12 17.2h.01" />
    </Svg>
  )
}

export function IconCheck(props: IconProps) {
  return (
    <Svg {...props} strokeWidth={2.25}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  )
}

export function IconUsers(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 5.6M18 14.4c2.1.7 3.5 2.6 3.5 5.1" />
    </Svg>
  )
}

export function IconRepeat(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 9a5 5 0 0 1 5-5h11l-3-3M20 15a5 5 0 0 1-5 5H4l3 3" />
    </Svg>
  )
}

export function IconDownload(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.5v11M7.5 10 12 14.5 16.5 10" />
      <path d="M4 17.5v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </Svg>
  )
}

export function IconChevronLeft(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m15 5-7 7 7 7" />
    </Svg>
  )
}

export function IconChevronRight(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m9 5 7 7-7 7" />
    </Svg>
  )
}

export function IconLink(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M10 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7l-1.4 1.4" />
      <path d="M14 10.5a4 4 0 0 0-5.7 0L5.5 13.3a4 4 0 0 0 5.7 5.7l1.4-1.4" />
    </Svg>
  )
}

export function IconStore(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 9.5V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5" />
      <path d="M3 9.5 4.8 4a1 1 0 0 1 1-.7h12.4a1 1 0 0 1 1 .7L21 9.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z" />
      <path d="M9.5 21v-6h5v6" />
    </Svg>
  )
}

export function IconList(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" strokeWidth={2.5} />
    </Svg>
  )
}

export function IconInbox(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 13.5h4l1.5 3h6l1.5-3h4" />
      <path d="M5.6 4.5h12.8a1 1 0 0 1 .95.68l2.15 6.4V19a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1v-7.42l2.15-6.4a1 1 0 0 1 .95-.68Z" />
    </Svg>
  )
}

/** Ονόματα προς component, για δεδομένα που έρχονται από το API. */
export const ICONS = {
  home: IconHome,
  truck: IconTruck,
  cart: IconCart,
  clipboard: IconClipboard,
  bell: IconBell,
  settings: IconSettings,
  plus: IconPlus,
  star: IconStar,
  trash: IconTrash,
  close: IconClose,
  search: IconSearch,
  clock: IconClock,
  calendar: IconCalendar,
  box: IconBox,
  alert: IconAlert,
  check: IconCheck,
  users: IconUsers,
  repeat: IconRepeat,
  download: IconDownload,
  chevronRight: IconChevronRight,
  link: IconLink,
  store: IconStore,
  inbox: IconInbox,
  list: IconList,
} as const

export type IconName = keyof typeof ICONS
